import { Overlay, type OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { DOCUMENT, type Injector, type Type } from '@angular/core';
import { type Observable, ReplaySubject, takeUntil } from 'rxjs';

import { ESCAPE_KEY } from '../constants/escape-key.constant';
import { activeHtmlElement } from '../utils/active-html-element.util';
import { CLARITY_PASSIVE_DIALOG_PANEL_CLASS } from './clarity-dialog-panel-class.constant';

/** What opening a passive dialog needs. Internal to the library, like the class itself. */
export type ClarityPassiveDialogRequest = {
	/** Resolves the CDK `Overlay` and the `DOCUMENT`. */
	readonly parentInjector: Injector;
	readonly content: Type<unknown>;
	/** Already carries `ClarityDialogRef` and `CLARITY_OVERLAY_DATA_TOKEN` for the body. */
	readonly contentInjector: Injector;
	readonly ariaLabel: string | undefined;
	/** `ClarityDialogConfig.width`, already resolved to a CSS length by the ref. */
	readonly width: string;
	/** The width bound for that width, already resolved to a CSS length by the ref. */
	readonly maxWidth: string;
	/** The height bound for that width, already resolved to a CSS length by the ref. */
	readonly maxHeight: string;
	/**
	 * `CLARITY_DIALOG_PANEL_CLASS` plus `ClarityDialogConfig.panelClass`, already resolved by the ref;
	 * `CLARITY_PASSIVE_DIALOG_PANEL_CLASS` is added here.
	 */
	readonly panelClasses: string[];
	/**
	 * `false` only when the caller refused a backdrop press outright. The dismissible intent itself
	 * arrives as {@link ClarityPassiveDialog.disableClose} and is read alongside this, so a dialog that
	 * refused nothing keeps following `setDismissible` in both directions.
	 */
	readonly backdropDismissible: boolean;
	/** `ClarityDialogConfig.closeOnNavigation`, defaulted by the ref. */
	readonly closeOnNavigation: boolean;
};

/** Brain's `Modal` stops every key at the dialog (`Modal.tsx:223-228`), so a key typed inside reaches no one above it. */
function stopKeyPropagation(event: KeyboardEvent): void {
	event.stopPropagation();
}

/** Ported from Clarity `apps/web/src/shared/ui/Modal.tsx` (commit `f9217bb5a`). */
/**
 * Brain's `Modal`, on a bare CDK overlay — the `passive` backend of `ClarityDialogRef`.
 *
 * Material's dialog cannot be configured into this: it always moves focus inside (`autoFocus: false`
 * still focuses its container), always installs its focus trap, and always subscribes to the CDK
 * keyboard dispatcher, which would swallow the Escape an approvals or ACP panel beneath it must also
 * see. So this is a pane, a backdrop and a scroll lock, and the rest is brain's:
 *
 * - **No focus move on open.** Brain's own focus timer is cancelled by its enter animation before it
 *   fires (`Modal.tsx:86-98, 138-154`), so focus stays on the trigger (brain defect #36).
 * - **Every key stops at the dialog.** Escape therefore closes it only while focus is outside, through
 *   the `document` listener below; with focus inside, Escape does nothing and reaches no other panel,
 *   and Tab / Shift+Tab are not confined.
 * - **A self-close restores focus.** Escape or a backdrop press returns focus to the element that had
 *   it at open (`Modal.tsx:100-110`); a close from the body does not.
 * - **The backdrop can be refused on its own.** `ClarityDialogConfig.backdropDismissible: false` stops
 *   the press here and leaves Escape untouched — brain's `closeOnBackdropClick={false}`. The Material
 *   backend has one flag for both and has to work around it; this one simply does not call `dismiss`.
 * - **Back / Forward closes it**, as the Material backend's `closeOnNavigation` does: brain's `Modal`
 *   goes away with the component that renders it, and a dialog that outlived its screen would keep
 *   an `onConfirm` bound to the entity that screen showed. A caller whose screen survives the
 *   navigation opts out with `closeOnNavigation: false`, and the dialog stays as brain's does.
 *
 * It exposes the three members of `MatDialogRef` that `ClarityDialogRef` drives — `close`,
 * `afterClosed` and a writable `disableClose` — so the ref runs both backends through one code path.
 */
export class ClarityPassiveDialog<R> {
	/** While `true`, Escape and a backdrop press do nothing — the same flag `MatDialogRef` carries. */
	public disableClose = false;

	private readonly closedSubject = new ReplaySubject<R | undefined>(1);
	private readonly overlayRef: OverlayRef;
	private readonly document: Document;
	/** Brain's `closeOnBackdropClick`: `false` refuses the press and says nothing about Escape. */
	private readonly backdropDismissible: boolean;
	/** The element that had focus at open — brain's `previousFocusRef` (`Modal.tsx:141-145`). */
	private readonly restoreTarget: HTMLElement | null;
	/** `Modal.tsx:156-194`: registered for as long as the dialog is on screen. */
	private readonly onDocumentKeydown = (event: KeyboardEvent): void => {
		if (event.key === ESCAPE_KEY) {
			this.dismiss();
		}
	};

	private isClosed = false;

	constructor(request: ClarityPassiveDialogRequest) {
		const overlay = request.parentInjector.get(Overlay);

		this.document = request.parentInjector.get(DOCUMENT);
		this.backdropDismissible = request.backdropDismissible;
		this.restoreTarget = activeHtmlElement(this.document);
		this.overlayRef = overlay.create({
			positionStrategy: overlay.position().global().centerHorizontally().centerVertically(),
			scrollStrategy: overlay.scrollStrategies.block(),
			hasBackdrop: true,
			panelClass: [...request.panelClasses, CLARITY_PASSIVE_DIALOG_PANEL_CLASS],
			width: request.width,
			maxWidth: request.maxWidth,
			maxHeight: request.maxHeight,
			disposeOnNavigation: request.closeOnNavigation,
		});
		this.describePane(request.ariaLabel);

		this.overlayRef
			.backdropClick()
			.pipe(takeUntil(this.closedSubject))
			.subscribe(() => this.dismissFromBackdrop());
		this.overlayRef
			.detachments()
			.pipe(takeUntil(this.closedSubject))
			.subscribe(() => this.close());
		this.document.addEventListener('keydown', this.onDocumentKeydown);

		this.overlayRef.attach(new ComponentPortal(request.content, null, request.contentInjector));
	}

	/** Emits once — the result, or `undefined` for a dismissal — and completes. Replays to a late subscriber. */
	public afterClosed(): Observable<R | undefined> {
		return this.closedSubject.asObservable();
	}

	/** Removes the dialog and reports `result`. A no-op once it is gone. Never moves focus. */
	public close(result?: R): void {
		if (this.isClosed) {
			return;
		}

		this.isClosed = true;
		this.document.removeEventListener('keydown', this.onDocumentKeydown);
		this.overlayRef.dispose();
		this.closedSubject.next(result);
		this.closedSubject.complete();
	}

	/**
	 * The pane is the dialog: brain's `Modal` renders `role="dialog"` with `aria-modal` on the element
	 * that holds the content (`Modal.tsx:213-217`). Set on the CDK pane directly, as the CDK sets its
	 * own pane attributes, so the body component stays the only thing rendered inside it.
	 */
	private describePane(ariaLabel: string | undefined): void {
		const pane = this.overlayRef.overlayElement;

		pane.setAttribute('role', 'dialog');
		pane.setAttribute('aria-modal', 'true');
		if (ariaLabel !== undefined) {
			pane.setAttribute('aria-label', ariaLabel);
		}
		pane.addEventListener('keydown', stopKeyPropagation);
	}

	/**
	 * A backdrop press, which a caller may refuse on its own — unlike Escape, which {@link disableClose}
	 * is the only switch for. Refusing it here rather than inside {@link dismiss} is what keeps the two
	 * separable: `disableClose` still means "nothing the user does dismisses this".
	 */
	private dismissFromBackdrop(): void {
		if (!this.backdropDismissible) {
			return;
		}

		this.dismiss();
	}

	/** Brain's `handleClose`: a self-close owes focus back to where it was (`Modal.tsx:100-121`). */
	private dismiss(): void {
		if (this.disableClose) {
			return;
		}

		this.close();
		if (this.restoreTarget?.isConnected === true) {
			this.restoreTarget.focus();
		}
	}
}
